<template>
  <base-modal
    :ref="modal.key"
    :show="open"
    :title="modalTitle"
    :subtitle="role.name"
    @on-confirm="saveItem"
    @on-cancel="hide()"
    :okText="$t('Save')"
    :closeText="$t('Close')"
    :maxWidth="700"
  >
    <v-tabs centered v-model="selectedTab">
      <v-tab>{{ $t("Description") }}</v-tab>
      <v-tab>{{ $t("FunctionRights") }}</v-tab>
      <v-tab>{{ $t("Users") }}</v-tab>
      <v-tab-item>
        <v-card flat class="my-0">
          <v-card-text>
            <v-form class="mt-0">
              <v-text-field
                v-bind:label="$t('Name')"
                v-model="role.name"
                maxlength="100"
                :counter="100"
                :error-messages="nameErrors"
                @input="$v.role.name.$touch()"
                @blur="$v.role.name.$touch()"
              ></v-text-field>

              <v-text-field
                v-bind:label="$t('Description')"
                v-model="role.description"
                maxlength="100"
                :counter="100"
                :error-messages="descriptionErrors"
                @input="$v.role.description.$touch()"
                @blur="$v.role.description.$touch()"
              ></v-text-field>
            </v-form>
          </v-card-text>
        </v-card>
      </v-tab-item>
      <v-tab-item>
        <v-card class="my-0">
          <v-card-text>
            <v-row>
              <BaseDualList
                :source="availablesFunctions"
                :destination="role.functionRights"
                item-text="text"
                item-id="id"
                @onChangeList="onFunctionRightListChange"
              />
            </v-row>
          </v-card-text>
        </v-card>
      </v-tab-item>
      <v-tab-item>
        <v-card class="my-0">
          <v-card-text>
            <v-row>
              <BaseDualList
                :source="availablesUsers"
                :destination="role.users"
                item-text="text"
                item-id="id"
                @onChangeList="onUserListChange"
              />
            </v-row>
          </v-card-text>
        </v-card>
      </v-tab-item>
    </v-tabs>
  </base-modal>
</template>
<script>
import { required } from "vuelidate/lib/validators";
import { validationMixin } from "vuelidate";

import RoleService from "@/services/RoleService";
import Role from "@/ecm/models/Role";
import Utils from "@/ecm/common/Utils";

export default {
  name: "RoleModal",
  created() {
    RoleService.getUserList(0).then((data) => {
      this.userList = data;
    });

    RoleService.getFunctionRightList().then((data) => {
      this.functionRightsList = data.map((item) => ({
        ...item,
        text: item.text,
      }));
    });
    this.$v.$reset();
  },
  data() {
    return {
      userList: [],
      availablesUsers: [],
      functionRightsList: [],
      availablesFunctions: [],
      modal: {
        title: this.$t("Role"),
        key: "roleModal",
      },
      open: false,
      role: new Role(),
      selectedTab: 0,
      isNew: true,
    };
  },
  mixins: [validationMixin],
  validations: {
    role: {
      name: { required },
      description: { required },
    },
  },
  computed: {
    modalInstance() {
      return this.$refs[this.modal.key];
    },
    modalTitle() {
      return this.modal.title + (this.role.name ? " - " + this.role.name : "");
    },
    descriptionErrors() {
      const errors = [];
      if (!this.$v.role.description.$dirty) return errors;
      !this.$v.role.description.required &&
        errors.push(this.$t("RequiredField"));
      return errors;
    },
    nameErrors() {
      const errors = [];
      if (!this.$v.role.name.$dirty) return errors;
      !this.$v.role.name.required && errors.push(this.$t("RequiredField"));
      return errors;
    },
  },
  methods: {
    onFunctionRightListChange: function ({ destination, source }) {
      this.availablesFunctions = source;
      this.role.functionRights = destination;
    },
    onUserListChange: function ({ destination, source }) {
      this.availablesUsers = source;
      this.role.users = destination;
    },
    saveItem() {
      this.$v.$touch();

      if (this.$v.$invalid) {
        this.selectedTab = 0;
        return;
      }
      if (!this.IsValid()) {
        this.selectedTab = 2;
        return;
      }

      this.modalInstance.progress(true);

      RoleService.save(this.role.toDto())
        .then((result) => {
          if (result) {
            if (this.isNew) {
              Utils.toast.success(
                this.$t("Role") + " " + this.$t("CreatedSuccessfully")
              );
            } else {
              Utils.toast.success(
                this.$t("Role") + " " + this.$t("UpdatedSuccessfully")
              );
            }
            this.hide();
            this.$emit("on-item-saved");
          }
        })
        .catch((error) => {
          // error
        })
        .then(() => {
          //always
          this.modalInstance.progress(false);
        });
    },
    IsValid() {
      if (this.role.users.length == 0) {
        Utils.toast.error(this.$t("ItemsSelectionError"));
        return false;
      }
      return true;
    },
    new() {
      this.role = new Role();
      this.availablesUsers = this.userList;
      this.availablesFunctions = this.functionRightsList;
      this.$v.$reset();
      this.show();
    },
    edit(dto) {
      this.role = new Role(dto);
      this.isNew = false;
      var availablesFunctions = [...this.functionRightsList];
      var availablesUsers = [...this.userList];

      for (let i = 0; i < this.role.functionRights.length; i++) {
        const selected = this.functionRightsList.find(
          (functionRight) => functionRight.id == this.role.functionRights[i].id
        );

        this.role.functionRights[i].text = selected.text;

        availablesFunctions = availablesFunctions.filter((x) => {
          return x.id != selected.id;
        });
      }

      for (let i = 0; i < this.role.users.length; i++) {
        const selected = this.userList.find(
          (user) => user.id == this.role.users[i].id
        );

        this.role.users[i].text = selected.text;

        availablesUsers = availablesUsers.filter((x) => {
          return x.id != selected.id;
        });
      }

      this.availablesFunctions = availablesFunctions;
      this.availablesUsers = availablesUsers;

      this.show();
    },
    show() {
      this.open = true;
      this.selectedTab = 0;
    },
    hide() {
      this.open = false;
      this.selectedTab = 0;
      this.isNew = true;
    },
  },
};
</script>
